import { rackingTypeAccessorConfig } from 'projectSections/designPanels/PanelModuleGrid/accessors/rackingType'
import { StudioSystemType } from 'types/global'
import { getSystemRoofType } from '../../compatibility'
import { setRelativeTiltForModuleGrids } from '../../utils'
import { getRailOrientationOptions, isNovaTiltActive } from './NovaInputForm'
import { NOVA_RAIL_ORIENTATION } from './novaLabels'
import { NOVA_TILT_RANGES } from './NovaTiltRanges'

const MAX_ORIENTATION_SYNC_ATTEMPTS = 1
const orientationSyncAttemptsBySystem = new Map<string, number>()

const TILE_ROOF_TYPES = new Set(['Tile Clay', 'Tile Concrete'])

function isTileRoofType(roofType: string | null): boolean {
  return roofType != null && TILE_ROOF_TYPES.has(roofType)
}

// Tile roofs tie railOrientation to panel portrait/landscape — keep every module grid aligned
// with the first grid. Cap redraw attempts so a failed sync cannot loop recalc.
function enforceUniformOrientation(system: StudioSystemType, roofType: string | null): void {
  if (!isTileRoofType(roofType)) return
  const grids = system.moduleGrids?.() ?? []
  if (grids.length < 2) return

  const targetOrientation = grids[0].moduleLayout()
  const hasMixed = grids.some((mg: any) => mg.moduleLayout() !== targetOrientation)
  if (!hasMixed) {
    orientationSyncAttemptsBySystem.delete(system.uuid)
    return
  }

  const attempts = orientationSyncAttemptsBySystem.get(system.uuid) ?? 0
  if (attempts >= MAX_ORIENTATION_SYNC_ATTEMPTS) return
  orientationSyncAttemptsBySystem.set(system.uuid, attempts + 1)

  grids.forEach((mg: any) => {
    if (mg.moduleLayout() !== targetOrientation) {
      window.editor.execute(
        new window.RedrawModulesCommand(mg, 'moduleLayout', targetOrientation, mg.moduleLayout())
      )
    }
  })
}

// Mirrors tiltOption options in NovaInputForm (tile, 3-channel, and landscape only allow flush mount).
function getAllowedNovaTiltOptions(system: StudioSystemType, roofType: string | null): string[] {
  const isTileRoof = roofType === 'Tile Clay' || roofType === 'Tile Concrete'
  const is3Channel = Number(system.integration_json?.nova?.channels) === 3
  if (isTileRoof || is3Channel) return ['No tilt']

  const hasLandscape = system.moduleGrids?.()?.some((mg: any) => mg.moduleLayout() === 'landscape')
  if (hasLandscape) return ['No tilt']

  return Object.keys(NOVA_TILT_RANGES)
}

function applyNovaFlushMount(system: StudioSystemType): void {
  setRelativeTiltForModuleGrids(system, 0)
  system.moduleGrids().forEach((mg: any) => {
    if (mg.getPanelConfiguration() !== 'STANDARD') {
      rackingTypeAccessorConfig.set({ moduleGrid: mg, value: 'STANDARD' })
    }
  })
}

function seedNovaRailOrientationIfNeeded(system: StudioSystemType, roofType: string | null): void {
  const isTileRoof = isTileRoofType(roofType)
  const railOptions = getRailOrientationOptions(isTileRoof, system)
  const allowedValues = railOptions.map((option) => String(option.value))
  const currentRailOrientation = String(system.integration_json?.nova?.railOrientation ?? '')
  const defaultValue = railOptions[0].value

  if (currentRailOrientation && allowedValues.includes(currentRailOrientation)) return

  const updatedJson = {
    ...system.integration_json,
    nova: { ...system.integration_json?.nova, railOrientation: defaultValue },
  }
  window.editor.execute(new window.SetValueCommand(system, 'integration_json', updatedJson))
}

function resetNovaTiltIfInvalid(system: StudioSystemType, roofType: string | null): void {
  const allowed = getAllowedNovaTiltOptions(system, roofType)
  const currentTilt = String(system.integration_json?.nova?.tiltOption ?? '')
  if (!currentTilt || allowed.includes(currentTilt)) return

  const updatedJson = {
    ...system.integration_json,
    nova: { ...system.integration_json?.nova, tiltOption: 'No tilt' },
  }
  window.editor.execute(new window.SetValueCommand(system, 'integration_json', updatedJson))
  applyNovaFlushMount(system)
}

/** When tilt is active, only 2 rails and short-side orientation are valid. */
export function enforceNovaTiltMountConstraints(system: StudioSystemType): void {
  if (!isNovaTiltActive(system)) return

  const nova = system.integration_json?.nova ?? {}
  const updates: Record<string, unknown> = {}

  if (Number(nova.channels) === 3) updates.channels = 2
  if (String(nova.railOrientation ?? '') !== NOVA_RAIL_ORIENTATION.shortSide) {
    updates.railOrientation = NOVA_RAIL_ORIENTATION.shortSide
  }

  if (Object.keys(updates).length === 0) return

  const updatedJson = {
    ...system.integration_json,
    nova: { ...nova, ...updates },
  }
  window.editor.execute(new window.SetValueCommand(system, 'integration_json', updatedJson))
}

export function novaOnApply(system: StudioSystemType): void {
  system.moduleGrids().forEach((moduleGrid) => {
    rackingTypeAccessorConfig.set({ moduleGrid, value: 'STANDARD' })
  })
  const roofType = getSystemRoofType()
  enforceUniformOrientation(system, roofType)
  enforceNovaTiltMountConstraints(system)
  seedNovaRailOrientationIfNeeded(system, roofType)
  resetNovaTiltIfInvalid(system, roofType)
}

export function novaOnSync(system: StudioSystemType): void {
  const roofType = getSystemRoofType()
  // Fix channels/orientation for active tilt before tilt validation (3-channel disallows tilt).
  enforceNovaTiltMountConstraints(system)
  // Run before orientation sync so landscape + tilt range is cleared while landscape is still detected.
  resetNovaTiltIfInvalid(system, roofType)

  enforceUniformOrientation(system, roofType)

  seedNovaRailOrientationIfNeeded(system, roofType)
  resetNovaTiltIfInvalid(system, roofType)

  const isTileRoof = isTileRoofType(roofType)
  const hasLandscape = system.moduleGrids?.()?.some((mg: any) => mg.moduleLayout() === 'landscape')
  const is3Channel = Number(system.integration_json?.nova?.channels) === 3

  if (isTileRoof || hasLandscape || is3Channel) {
    applyNovaFlushMount(system)
  }
}
